import { captureMessage } from '@sentry/browser';
import React from 'react';
import { useTranslate } from '../../mixins/translate/translate';
import { fetchGet } from '../../store/fetch';
import { AssetTypes as assetTypes } from '../../utils/enums/AssetTypes';
import { formatBytes } from '../../utils/formatBytes';
import { getAssetUrl } from '../../utils/getAssetUrl';
import { IconButton } from '../common/iconButtons/IconButton';
import { IconEditButton } from '../common/iconButtons/IconEditButton';
import { IconRemoveButton } from '../common/iconButtons/IconRemoveButton';
import { Modal } from '../common/Modal/Modal';
import {
    EditPanel,
    EditPanelButton,
    EditPanelButtons,
    EditPanelCell,
    EditPanelFilterCreateSelect,
    EditPanelRow,
    Table,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableHeadRow,
    TableRow,
} from '../elements';
import { NullableFormattedDate } from './NullableFormattedDate';
import { showAssetInNewWindow } from './showAssetInNewWindow';
import { TagLabels } from './TagLabels';

export interface IAsset {
    date: string | Date;
    type: string | assetTypes;
    size: number;
    name: string;
    id: string;
    labels?: undefined | string[];
    objectId?: undefined | string | null;
    filenameThumbnail?: undefined | string | null;
}

const openAsset = (asset: IAsset, objectId?: string, surveyId?: string, equipmentId?: string) => {
    if (asset.type === assetTypes.MeasurementPDF || asset.type === assetTypes.Image || asset.type === assetTypes.Image360) {
        showAsset(asset, objectId, surveyId, equipmentId);
    } else {
        // TODO: ZXY-1209 clean up if bug is fixed
        if (!window?.location?.href) {
            captureMessage('window.location.href is undefined');
        }
        window.location.href = getAssetUrl(asset, { forceDownload: true, objectId, surveyId });
    }
};

const showAsset = (asset: IAsset, objectId?: string, surveyId?: string, equipmentId?: string) => {
    showAssetInNewWindow(asset, { objectId, surveyId, equipmentId });
};

type DocumentsListProps = {
    assets: IAsset[];
    objectId?: string | undefined;
    surveyId?: string | undefined;
    equipmentId?: string;
    blueHeader?: boolean;
    manageLabels?: boolean;
    onRemove?: (assetId: string) => void;
    onUpdate?: (assetId: string, data: { labels?: string[]; name?: string }) => void;
};

// eslint-disable-next-line @typescript-eslint/naming-convention
export const DocumentsList = (props: DocumentsListProps) => {
    const translate = useTranslate();
    const [isEditFilename, setIsEditFilename] = React.useState(false);
    const [isLabelsModalShown, setIsLabelsModalShown] = React.useState<boolean>(false);
    const [editableAsset, setEditableAsset] = React.useState<IAsset | undefined>();

    const updateFileName = (assetId: string, name: string) => {
        if (props.onUpdate) {
            props.onUpdate(assetId, { name });
        }

        setIsEditFilename(false);
        setEditableAsset(undefined);
    };

    const showAssetNameInput = (asset: IAsset) => {
        setEditableAsset(asset);
        setIsEditFilename(true);
    };

    const showEditLabelsModal = (asset: IAsset) => {
        setEditableAsset(asset);
        setIsLabelsModalShown(true);
    };

    const onModalClose = () => {
        setIsLabelsModalShown(false);
        setEditableAsset(undefined);
    };

    return (
        <>
            <Table hover>
                <TableHead>
                    <TableHeadRow light={!props.blueHeader}>
                        <TableHeader>{translate('common.filename')}</TableHeader>
                        <TableHeader>{translate('common.type')}</TableHeader>
                        <TableHeader>{translate('common.size')}</TableHeader>
                        <TableHeader>{translate('common.date')}</TableHeader>
                        {(props.onRemove || props.onUpdate) && <TableHeader>&nbsp;</TableHeader>}
                    </TableHeadRow>
                </TableHead>
                <TableBody>
                    {props.assets?.map((asset) => (
                        <TableRow key={asset.id} onClick={() => openAsset(asset, props.objectId, props.surveyId, props.equipmentId)}>
                            <TableData>
                                {isEditFilename && asset.id === editableAsset?.id ? (
                                    <input
                                        type="text"
                                        name="titleClip"
                                        id="titleClip"
                                        className="gui-input fs13"
                                        placeholder={translate('video.titleInput')}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }}
                                        defaultValue={asset.name}
                                        onBlur={(e) => updateFileName(asset.id, e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                updateFileName(asset.id, e.currentTarget.value);
                                            }
                                        }}
                                    />
                                ) : (
                                    <>
                                        {asset.name}
                                        {Array.isArray(asset.labels) && <TagLabels labels={asset.labels} />}
                                    </>
                                )}
                            </TableData>
                            <TableData>{translate(`assetType.${asset.type}`)}</TableData>
                            <TableData nowrap>{formatBytes(asset.size)}</TableData>
                            <TableData nowrap>
                                <NullableFormattedDate value={asset.date} />
                            </TableData>
                            {(props.onRemove || props.onUpdate) && (
                                <TableData alignRight nowrap>
                                    {props.onRemove && (
                                        <IconRemoveButton
                                            removeQuestion={translate('common.confirmDelete', asset.name)}
                                            onClick={() => (props.onRemove ? props.onRemove(asset.id) : console.error('onRemove not defined'))}
                                        />
                                    )}
                                    {props.manageLabels && <IconButton iconClass="fa-tags" onClick={() => showEditLabelsModal(asset)} />}
                                    {props.onUpdate && <IconEditButton onClick={() => showAssetNameInput(asset)} />}
                                </TableData>
                            )}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            {isLabelsModalShown && editableAsset && props.onUpdate && (
                <EditDocumentLabelsModal asset={editableAsset} onClose={onModalClose} onSave={props.onUpdate} />
            )}
        </>
    );
};

type EditDocumentLabelsModalProps = {
    asset: IAsset;
    onSave: (assetId: string, data: { labels: string[] }) => void;
    onClose: () => void;
};

function EditDocumentLabelsModal(props: EditDocumentLabelsModalProps) {
    const translate = useTranslate();
    const [knownLabels, setKnownLabels] = React.useState<string[]>([]);
    const [selectedLabels, setSelectedLabels] = React.useState<string[]>(props.asset.labels ?? []);

    React.useEffect(() => {
        const loadAndStoreKnownLabels = async () => {
            const url = `/api/objects/${props.asset.objectId}/assets/knownLabels`;
            const result = await fetchGet(url);
            setKnownLabels(result?.json);
        };

        void loadAndStoreKnownLabels();
    }, [props.asset.objectId]);

    const saveClickHandler = () => {
        props.onSave(props.asset.id, { labels: selectedLabels });
        props.onClose();
    };

    return (
        <Modal id="dialog" onClose={() => props.onClose()}>
            <Modal.Title>
                <h3>
                    <span className="panel-title">{translate('asset.manageLabelsModal')}</span>
                </h3>
            </Modal.Title>

            <Modal.Content style={{ height: '150px' }}>
                <div className="admin-form" style={{ width: '600px' }}>
                    <EditPanel>
                        <EditPanelRow>
                            <EditPanelCell rowCount={12}>
                                <EditPanelFilterCreateSelect
                                    topLabel={translate('asset.addRemoveLabels', props.asset.name)}
                                    name="labels"
                                    placeholder={translate('common.selectItem', translate('common.labels'))}
                                    value={selectedLabels}
                                    options={knownLabels}
                                    handleChange={setSelectedLabels}
                                />
                            </EditPanelCell>
                        </EditPanelRow>
                    </EditPanel>
                </div>
            </Modal.Content>

            <Modal.Actions>
                <EditPanelButtons fixWidth={true}>
                    <EditPanelButton small={true} text={translate('common.cancel')} onClick={() => props.onClose()} />
                    <EditPanelButton small={true} text={translate('common.save')} onClick={saveClickHandler} primary />
                </EditPanelButtons>
            </Modal.Actions>
        </Modal>
    );
}
